// src/components/Documents.js
import React, { useEffect, useState, useContext, useMemo, useRef, useCallback } from 'react';
import { CustomerContext } from '../App';
import { auth } from '../firebaseClient';
import { onAuthStateChanged } from 'firebase/auth';
import { useDebounce } from '../hooks/useDebounce';
import './Documents.css';
import previewIcon from '../assets/vorschau.png';
import downloadIcon from '../assets/download.png';
import ToastNotification from './messages/ToastNotification';
import PreviewInfoBox from './messages/PreviewInfoBox';

/** Prüft, ob es ein iOS-Gerät ist */
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function Documents() {
      // 0) Admin-Flag initialisieren
  const [isAdmin, setIsAdmin] = useState(false);
      // 1) Context-ID immer holen, aber nur bei Nicht-Admin verwenden
  const { selectedCustomerId: ctxCustomerId } = useContext(CustomerContext);
  const selectedCustomerId = isAdmin ? null : ctxCustomerId;
  // --- State-Variablen für Dokumente + Filter + Paging + Upload + Vorschaubild ---
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
    // Suche & Filter-Stati
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 300);

  const [filterCustomer, setFilterCustomer] = useState('Alle Kunden');
  const [filterStreet, setFilterStreet] = useState('Alle Wohnungen');
  const [filterType, setFilterType] = useState('Alle Typen');
  const [filterYear, setFilterYear] = useState('Alle Jahre');
  const [filterMonth, setFilterMonth] = useState('Alle Monate');
  // Sortierung
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' oder 'desc'
  // Infinite Scroll: wie viele Einträge aktuell sichtbar sind
  const [visibleCount, setVisibleCount] = useState(100);
  const loadMoreRef = useRef(null);
  // Vorschaulink (Overlay)
  const [previewLink, setPreviewLink] = useState(null);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadDocName, setUploadDocName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadUserMessage, setUploadUserMessage] = useState('');

  // Admin Upload States
  const [adminCustomer, setAdminCustomer] = useState('');
  const [adminNewCustomer, setAdminNewCustomer] = useState('');
  const [adminStreet, setAdminStreet] = useState('');
  const [adminNewStreet, setAdminNewStreet] = useState('');
  const [adminDate, setAdminDate] = useState(new Date().toISOString().slice(0, 10));
  const [adminDocType, setAdminDocType] = useState('');
  const [adminNewDocType, setAdminNewDocType] = useState('');
  const [adminLabel, setAdminLabel] = useState('');
  const [adminFile, setAdminFile] = useState(null);
  
  // Toast-Benachrichtigungen State
  const [toast, setToast] = useState(null);
  
  // Umgebungs-Variablen und Admin-E-Mail-Liste
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  const adminEmails = ['admin@prosperly.com', 'prosperly.info@gmail.com'];

  // Lokales Cache-Objekt (im Speicher)
  const cacheRef = useRef({});
  // Request-Deduplizierung: Verhindert mehrere gleichzeitige Requests für dieselbe URL
  const pendingRequestsRef = useRef({});
  // Eindeutiger Session-Key für Cache-Isolation zwischen Benutzern
  const sessionKeyRef = useRef(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  // Paging-Run-ID + AbortController für sauberes Abbrechen bei Filterwechsel
  const pagingRunRef = useRef(0);
  const pagingAbortRef = useRef(null);

  /** cachedFetch mit localStorage-Fallback und Request-Deduplizierung */
  async function cachedFetch(url) {
    // Prüfe ob bereits ein Request für diese URL läuft
    if (pendingRequestsRef.current[url]) {
      // Warte auf den laufenden Request
      return pendingRequestsRef.current[url];
    }

    // Prüfe Memory-Cache zuerst
    const cacheKey = `${sessionKeyRef.current}_${url}`;
    if (cacheRef.current[cacheKey]) {
      return cacheRef.current[cacheKey];
    }

    // Prüfe localStorage (mit Session-Key für Isolation)
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Prüfe ob Cache zu alt ist (max 5 Minuten)
        const cacheTime = localStorage.getItem(`${cacheKey}_time`);
        if (cacheTime && Date.now() - parseInt(cacheTime) < 5 * 60 * 1000) {
          cacheRef.current[cacheKey] = parsed;
          return parsed;
        } else {
          // Cache zu alt, entfernen
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(`${cacheKey}_time`);
        }
      }
    } catch (e) {
      // localStorage-Fehler ignorieren
    }

    // Erstelle neuen Request mit Promise
    const requestPromise = (async () => {
      try {
        const resp = await fetch(url);
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          const err = new Error(data?.message || `HTTP ${resp.status}: ${resp.statusText}`);
          err.status = resp.status;
          err.body = data;
          throw err;
        }
        if (Array.isArray(data)) {
          cacheRef.current[cacheKey] = data;
          try {
            localStorage.setItem(cacheKey, JSON.stringify(data));
            localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
          } catch (e) {
            // localStorage voll oder nicht verfügbar - ignoriere
          }
        }
        return data;
      } finally {
        // Entferne Request aus pending nach Abschluss
        delete pendingRequestsRef.current[url];
      }
    })();

    // Speichere Promise für Deduplizierung
    pendingRequestsRef.current[url] = requestPromise;
    return requestPromise;
  }

  /** Minimaler Cache nur für die erste Seite paged (Objekt-Response) */
  async function cachedFetchFirstPagePaged(url, { signal } = {}) {
    const cacheKey = `${sessionKeyRef.current}_paged_first_${url}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cacheTime = localStorage.getItem(`${cacheKey}_time`);
        if (cacheTime && Date.now() - parseInt(cacheTime, 10) < 2 * 60 * 1000) {
          return parsed;
        }
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_time`);
      }
    } catch (e) {
      // ignore cache errors
    }

    const resp = await fetch(url, { signal });
    const data = await resp.json().catch(() => null);
    if (!resp.ok) {
      const err = new Error(data?.message || `HTTP ${resp.status}: ${resp.statusText}`);
      err.status = resp.status;
      err.body = data;
      throw err;
    }
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(`${cacheKey}_time`, Date.now().toString());
    } catch (e) {
      // ignore
    }
    return data;
  }

  /** Cache für eine URL invalidieren */
  function invalidateCache(url) {
    const cacheKey = `${sessionKeyRef.current}_${url}`;
    delete cacheRef.current[cacheKey];
    try {
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}_time`);
    } catch (e) {
      // Ignoriere Fehler
    }
  }

  // Admin-Status erkennen
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => {
      setIsAdmin(!!(user && adminEmails.includes(user.email)));
    });
    return () => unsub();
  }, []);

 // --- Globale Funktion: PDFs laden mit Caching ---
  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    // Abbrechen/Invalidate laufenden Paging-Runs
    pagingRunRef.current += 1;
    const runId = pagingRunRef.current;
    if (pagingAbortRef.current) {
      try { pagingAbortRef.current.abort(); } catch (e) {}
    }
    const abortController = new AbortController();
    pagingAbortRef.current = abortController;
    const signal = abortController.signal;

    // Bestimme ob wir summaryOnly verwenden sollen:
    // - summaryOnly: NUR wenn "Alle Kunden" UND "Alle Wohnungen" ausgewählt sind
    // - vollständige Daten: wenn ein spezifischer Kunde ODER eine spezifische Straße ausgewählt ist
    // WICHTIG: Wenn eine Straße ausgewählt ist, IMMER vollständige Daten laden (kein summaryOnly)
    const useSummaryOnly = isAdmin && 
      filterCustomer === 'Alle Kunden' && 
      filterStreet === 'Alle Wohnungen';

    // Paged Endpoint für schnelleres initiales Laden
    let url = `${API_URL}/api/readvpdfsPaged`;
    if (isAdmin) {
      if (filterCustomer && filterCustomer !== 'Alle Kunden') {
        // Spezifischer Kunde: vollständige Daten
        url += `?customerId=${encodeURIComponent(filterCustomer)}`;
      } else {
        // Alle Kunden: summaryOnly nur wenn auch alle Wohnungen
        // Wenn eine Straße ausgewählt ist, lade IMMER vollständige Daten (kein summaryOnly)
        if (useSummaryOnly) {
          url += '?customerId=all&summaryOnly=1';
        } else {
          // Wenn eine Straße ausgewählt ist, lade alle Daten (vollständig, kein summaryOnly)
          url += '?customerId=all';
        }
      }
    } else if (selectedCustomerId) {
      // Normale User: vollständige Daten (haben nur einen Kunden)
      url += `?customerId=${encodeURIComponent(selectedCustomerId)}`;
    } else {
      setAllDocs([]);
      setLoading(false);
      return;
    }

    try {
      const initialTarget = 250;
      const pageSize = 200;
      const baseUrl = `${url}&pageSize=${pageSize}`;

      // Erste Seite optional cachen (Option A)
      const firstPage = await cachedFetchFirstPagePaged(baseUrl, { signal });
      const firstItems = Array.isArray(firstPage?.items) ? firstPage.items : [];
      let nextCursor = firstPage?.nextCursor || null;

      // Bis ca. 1000 sammeln (ohne zusätzliche Sortierung)
      let collected = [...firstItems];
      while (collected.length < initialTarget && nextCursor) {
        if (runId !== pagingRunRef.current) return;
        const pageUrl = `${baseUrl}&cursor=${nextCursor}`;
        const resp = await fetch(pageUrl, { signal });
        const data = await resp.json().catch(() => null);
        if (!resp.ok) {
          const err = new Error(data?.message || `HTTP ${resp.status}: ${resp.statusText}`);
          err.status = resp.status;
          err.body = data;
          throw err;
        }
        const items = Array.isArray(data?.items) ? data.items : [];
        collected = collected.concat(items);
        nextCursor = data?.nextCursor || null;
      }

      if (runId !== pagingRunRef.current) return;
      setAllDocs(collected);
      setLoading(false);

      // Rest im Hintergrund nachladen
      (async () => {
        let cursor = nextCursor;
        const seen = new Set(collected.map(d => d?.name).filter(Boolean));
        while (cursor) {
          if (runId !== pagingRunRef.current) return;
          try {
            const pageUrl = `${baseUrl}&cursor=${cursor}`;
            const resp = await fetch(pageUrl, { signal });
            const data = await resp.json().catch(() => null);
            if (!resp.ok) {
              console.warn('Background paging error:', data?.message || resp.statusText);
              return;
            }
            const items = Array.isArray(data?.items) ? data.items : [];
            const unique = items.filter(it => {
              const n = it?.name;
              if (!n) return false;
              if (seen.has(n)) return false;
              seen.add(n);
              return true;
            });
            if (unique.length) {
              setAllDocs(prev => prev.concat(unique));
            }
            cursor = data?.nextCursor || null;
          } catch (e) {
            return;
          }
        }
      })();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Fehler beim Laden der Dokumente:', err);
      const message = err.body?.message || err.body?.error || err.message;
      const code = err.body?.code;
      setLoadError({ message: message || 'Dokumente konnten nicht geladen werden.', code });
      // Versuche gecachte Daten zu laden als Fallback
      try {
        const cacheKey = `${sessionKeyRef.current}_${url}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            setAllDocs(parsed);
            setLoadError(null);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        // Cache-Fallback fehlgeschlagen
      }
      setAllDocs([]);
    }
  }, [isAdmin, selectedCustomerId, filterCustomer, filterStreet, API_URL]);

  // Dokumente laden wenn sich Abhängigkeiten ändern
  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  // Bestimme ob wir im summaryOnly-Modus sind (nur minimale Anzeige)
  // Jetzt mit Straße: Dokumentenname, Kunde, Straße (aber keine Aktionen)
  const isSummaryMode = isAdmin && 
    filterCustomer === 'Alle Kunden' && 
    filterStreet === 'Alle Wohnungen';


  // Monatnamen
  const monthMap = useMemo(() => ({
    '01': 'Januar', '02': 'Februar', '03': 'März', '04': 'April',
    '05': 'Mai', '06': 'Juni', '07': 'Juli', '08': 'August',
    '09': 'September', '10': 'Oktober', '11': 'November', '12': 'Dezember'
  }), []);

  // Dateiname parsen
  function parseDoc(item) {
    // Unterstützt sowohl summaryOnly (nur name) als auch vollständige Dokumente
    const name = item.name || null;
    if (!name || !name.endsWith('.pdf')) return null;
    const parts = name.replace('.pdf', '').split('_');
    if (parts.length < 6 || parts[0] !== 'readv') return null;

    const [, customerId, streetHn, dateRaw, docType, ...rest] = parts;
    const docName = rest.join('_');
    let dateDE = 'unbekannt';
    if (dateRaw?.length === 8) {
      dateDE = `${dateRaw.slice(6, 8)}.${dateRaw.slice(4, 6)}.${dateRaw.slice(0, 4)}`;
    }
    // Behalte alle ursprünglichen Felder (downloadUrl, size, etc.) falls vorhanden
    return { ...item, name, customerId, streetHn, dateRaw, dateDE, docType, docName };
  }

  const parsedAll = useMemo(() => {
    return (Array.isArray(allDocs) ? allDocs : []).map(parseDoc).filter(Boolean);
  }, [allDocs]);

  const filteredDocs = useMemo(() => {
    const lowerSearch = debouncedSearchText.trim().toLowerCase();
    let filtered = parsedAll.filter(item => {
      // Admin-Filter: Wenn ein spezifischer Kunde ausgewählt ist
      if (isAdmin) {
        if (filterCustomer !== 'Alle Kunden' && item.customerId !== filterCustomer) return false;
      } else {
        // Normale User: Nur ihre eigenen Dokumente
        if (!selectedCustomerId || item.customerId !== selectedCustomerId) return false;
      }
      
      // Suchtext-Filter
      if (lowerSearch && !(
        item.docName.toLowerCase().includes(lowerSearch) ||
        item.streetHn.toLowerCase().includes(lowerSearch)
      )) return false;

      // Straßen-Filter (WICHTIG: Funktioniert auch bei "Alle Kunden")
      // Wenn eine Straße ausgewählt ist, zeige nur Dokumente dieser Straße
      // Dies funktioniert unabhängig davon, ob ein Kunde ausgewählt ist oder "Alle Kunden"
      if (filterStreet !== 'Alle Wohnungen' && item.streetHn !== filterStreet) return false;
      
      // Weitere Filter
      if (filterType !== 'Alle Typen' && item.docType !== filterType) return false;
      if (filterYear !== 'Alle Jahre' && item.dateRaw?.slice(0, 4) !== filterYear) return false;
      const monthKey = item.dateRaw?.slice(4, 6);
      if (filterMonth !== 'Alle Monate' && monthMap[monthKey] !== filterMonth) return false;
      return true;
    });

    // Sortierung anwenden
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aVal, bVal;
        
        switch (sortColumn) {
          case 'docName':
            aVal = a.docName?.toLowerCase() || '';
            bVal = b.docName?.toLowerCase() || '';
            break;
          case 'customerId':
            aVal = a.customerId?.toLowerCase() || '';
            bVal = b.customerId?.toLowerCase() || '';
            break;
          case 'streetHn':
            aVal = a.streetHn?.toLowerCase() || '';
            bVal = b.streetHn?.toLowerCase() || '';
            break;
          case 'docType':
            aVal = a.docType?.toLowerCase() || '';
            bVal = b.docType?.toLowerCase() || '';
            break;
          case 'date':
            // Sortiere nach dateRaw (YYYYMMDD Format)
            aVal = a.dateRaw || '00000000';
            bVal = b.dateRaw || '00000000';
            break;
          case 'month':
            // Sortiere nach Monat (01-12)
            aVal = a.dateRaw?.slice(4, 6) || '00';
            bVal = b.dateRaw?.slice(4, 6) || '00';
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [parsedAll, debouncedSearchText, filterCustomer, filterStreet, filterType, filterYear, filterMonth, isAdmin, selectedCustomerId, monthMap, sortColumn, sortDirection]);

  // Reset visible count wenn sich Filter ändert
  useEffect(() => {
    setVisibleCount(100);
  }, [filterCustomer, filterStreet, filterType, filterYear, filterMonth]);

  // Lazy Loading mit IntersectionObserver
  useEffect(() => {
    if (!loadMoreRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleCount < filteredDocs.length) {
        setVisibleCount(prev => Math.min(prev + 100, filteredDocs.length));
      }
    });
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [filteredDocs.length, visibleCount]);

  // Sortier-Funktion für Spalten-Header
  function handleSort(column) {
    if (sortColumn === column) {
      // Toggle Richtung wenn gleiche Spalte
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  // Sortier-Icon anzeigen
  function getSortIcon(column) {
    if (sortColumn !== column) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  }

  // Dropdown Optionen (alphabetisch sortiert)
  // WICHTIG: Verwende parsedAll (alle geladenen Dokumente), nicht filteredDocs
  // Gelöschte Dokumente werden automatisch aus allDocs entfernt, wenn sie nicht mehr existieren
  // Dadurch erscheinen sie auch nicht mehr in den Dropdowns
  const docCustomers = useMemo(() => 
    Array.from(new Set(parsedAll.map(d => d.customerId)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' })), 
    [parsedAll]
  );
  const docStreets = useMemo(() => 
    Array.from(new Set(parsedAll.map(d => d.streetHn)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' })), 
    [parsedAll]
  );
  const docTypes = useMemo(() => 
    Array.from(new Set(parsedAll.map(d => d.docType)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' })), 
    [parsedAll]
  );
  const years = useMemo(() => 
    Array.from(new Set(parsedAll.map(d => d.dateRaw?.slice(0, 4))))
      .filter(Boolean)
      .sort((a, b) => b.localeCompare(a)), // Neueste zuerst
    [parsedAll]
  );
  const months = useMemo(() => Object.values(monthMap), [monthMap]);

  // Lade Details für ein Dokument nach (wenn downloadUrl fehlt)
  async function fetchDocDetails(doc) {
    if (doc.downloadUrl) return doc; // Bereits vorhanden
    
    try {
      // Lade Details für diesen Kunden (ohne summaryOnly, um vollständige Daten zu bekommen)
      const docName = doc.name || `${doc.customerId}_${doc.streetHn}_${doc.dateRaw}_${doc.docType}_${doc.docName}.pdf`;
      const url = `${API_URL}/api/readvpdfs?customerId=${encodeURIComponent(doc.customerId)}`;
      
      // Cache für diese URL invalidieren, um frische Daten zu bekommen
      invalidateCache(url);
      
      const response = await fetch(url);
      if (!response.ok) {
        // Wenn 404 oder ähnliches, Dokument wurde möglicherweise gelöscht
        if (response.status === 404) {
          // Entferne Dokument aus allDocs
          setAllDocs(prev => prev.filter(item => {
            const itemName = item.name || '';
            return itemName !== doc.name && 
                   item.customerId !== doc.customerId ||
                   item.streetHn !== doc.streetHn ||
                   item.dateRaw !== doc.dateRaw;
          }));
          throw new Error('Dokument wurde nicht gefunden (möglicherweise gelöscht)');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('API hat kein Array zurückgegeben');
      }
      
      // Verbesserte Dokumentensuche: Normalisiere Namen für besseren Vergleich
      const normalizeName = (name) => {
        if (!name) return '';
        return name.replace(/\.pdf$/i, '').trim().toLowerCase();
      };
      
      const normalizedDocName = normalizeName(docName);
      const normalizedOriginalName = normalizeName(doc.name);
      
      // Suche nach dem Dokument anhand des Namens
      const fullDoc = data.find(d => {
        if (!d || !d.name) return false;
        const dNameNormalized = normalizeName(d.name);
        
        // Exakte Übereinstimmung
        if (dNameNormalized === normalizedDocName || dNameNormalized === normalizedOriginalName) {
          return true;
        }
        
        // Teilübereinstimmung: Prüfe ob alle wichtigen Teile enthalten sind
        if (doc.customerId && doc.streetHn && doc.dateRaw) {
          const hasCustomerId = d.name.includes(doc.customerId);
          const hasStreet = d.name.includes(doc.streetHn);
          const hasDate = d.name.includes(doc.dateRaw);
          if (hasCustomerId && hasStreet && hasDate) {
            return true;
          }
        }
        
        return false;
      });
      
      if (fullDoc && fullDoc.downloadUrl) {
        // Aktualisiere das Dokument in allDocs
        setAllDocs(prev => prev.map(item => {
          const itemNameNormalized = normalizeName(item.name);
          const matchNameNormalized = normalizedDocName || normalizedOriginalName;
          
          if (itemNameNormalized === matchNameNormalized) {
            return { ...item, ...fullDoc };
          }
          
          // Auch nach customerId, streetHn und dateRaw matchen
          if (item.customerId === doc.customerId && 
              item.streetHn === doc.streetHn && 
              item.dateRaw === doc.dateRaw) {
            return { ...item, ...fullDoc };
          }
          
          return item;
        }));
        return { ...doc, ...fullDoc };
      } else if (fullDoc && !fullDoc.downloadUrl) {
        console.warn('Dokument gefunden, aber keine downloadUrl:', fullDoc);
        throw new Error('Dokument gefunden, aber Download-URL fehlt');
      } else {
        // Dokument nicht gefunden - möglicherweise gelöscht
        // Entferne aus allDocs
        setAllDocs(prev => prev.filter(item => {
          const itemName = item.name || '';
          return itemName !== doc.name && 
                 (item.customerId !== doc.customerId ||
                  item.streetHn !== doc.streetHn ||
                  item.dateRaw !== doc.dateRaw);
        }));
        throw new Error('Dokument wurde nicht gefunden (möglicherweise gelöscht)');
      }
    } catch (err) {
      console.error('Fehler beim Laden der Details:', err);
      // Cache invalidieren bei Fehler
      const url = `${API_URL}/api/readvpdfs?customerId=${encodeURIComponent(doc.customerId)}`;
      invalidateCache(url);
      throw err; // Weiterwerfen für bessere Fehlerbehandlung
    }
  }

  async function handleDownload(doc) {
    try {
      // Wenn downloadUrl fehlt, lade Details nach
      const docWithUrl = await fetchDocDetails(doc);
      if (!docWithUrl.downloadUrl) {
        showToast('Download-URL konnte nicht geladen werden. Bitte versuchen Sie es später erneut.', 'error');
        // Cache invalidieren und neu laden
        const url = `${API_URL}/api/readvpdfs?customerId=${encodeURIComponent(doc.customerId)}`;
        invalidateCache(url);
        await fetchDocs();
        return;
      }
      
      const u = encodeURIComponent(docWithUrl.downloadUrl);
      const n = encodeURIComponent(docWithUrl.docName || doc.docName || 'document');
      const dl = `${API_URL}/api/downloadpdf?fileUrl=${u}&fileName=${n}`;
      
      // Versuche Download mit Fehlerbehandlung
      try {
        const a = document.createElement('a');
        a.href = dl;
        a.setAttribute('download', `${docWithUrl.docName || doc.docName || 'document'}.pdf`);
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Erfolgs-Toast
        showToast(`Download gestartet: ${docWithUrl.docName || doc.docName}`, 'success');
      } catch (downloadErr) {
        // Wenn Download fehlschlägt, könnte das Dokument gelöscht sein
        console.error('Download-Fehler:', downloadErr);
        const url = `${API_URL}/api/readvpdfs?customerId=${encodeURIComponent(doc.customerId)}`;
        invalidateCache(url);
        // Entferne Dokument aus allDocs
        setAllDocs(prev => prev.filter(item => {
          const itemName = item.name || '';
          return itemName !== doc.name && 
                 (item.customerId !== doc.customerId ||
                  item.streetHn !== doc.streetHn ||
                  item.dateRaw !== doc.dateRaw);
        }));
        // Neu laden
        await fetchDocs();
        throw new Error('Dateifehler: Dokument wurde möglicherweise gelöscht. Die Liste wurde aktualisiert.');
      }
    } catch (err) {
      console.error('Download-Fehler:', err);
      const errorMessage = err.message || 'Dateifehler beim Download. Bitte versuchen Sie es später erneut.';
      showToast(errorMessage, 'error');
    }
  }

  // Vorschau (Preview) im Overlay
  async function handlePreview(doc) {
    try {
      // Wenn downloadUrl fehlt, lade Details nach
      const docWithUrl = await fetchDocDetails(doc);
      if (!docWithUrl.downloadUrl) {
        showToast('Vorschau-URL konnte nicht geladen werden. Bitte versuchen Sie es später erneut.', 'error');
        // Cache invalidieren und neu laden
        const url = `${API_URL}/api/readvpdfs?customerId=${encodeURIComponent(doc.customerId)}`;
        invalidateCache(url);
        await fetchDocs();
        return;
      }
      
      const u = encodeURIComponent(docWithUrl.downloadUrl);
      const fileName = docWithUrl.docName || doc.docName || 'document';
      const prev = `${API_URL}/api/previewpdf?fileUrl=${u}&fileName=${encodeURIComponent(fileName)}`;
      
      // Versuche Preview zu öffnen
      if (isIOS()) {
        window.open(prev, '_blank');
      } else {
        setPreviewLink(prev);
      }
      
      // Prüfe nach kurzer Zeit ob Preview geladen wurde (für Fehlerbehandlung)
      setTimeout(async () => {
        // Wenn Preview nach 3 Sekunden noch nicht geladen wurde, könnte es ein Fehler sein
        // Aber wir können das nicht zuverlässig prüfen, daher nur Logging
      }, 3000);
      
    } catch (err) {
      console.error('Preview-Fehler:', err);
      const errorMessage = err.message || 'Dateifehler bei der Vorschau. Bitte versuchen Sie es später erneut.';
      showToast(errorMessage, 'error');
      
      // Cache invalidieren und neu laden bei Fehler
      const url = `${API_URL}/api/readvpdfs?customerId=${encodeURIComponent(doc.customerId)}`;
      invalidateCache(url);
      // Entferne Dokument aus allDocs wenn es nicht gefunden wurde
      if (errorMessage.includes('nicht gefunden') || errorMessage.includes('gelöscht')) {
        setAllDocs(prev => prev.filter(item => {
          const itemName = item.name || '';
          return itemName !== doc.name && 
                 (item.customerId !== doc.customerId ||
                  item.streetHn !== doc.streetHn ||
                  item.dateRaw !== doc.dateRaw);
        }));
      }
      await fetchDocs();
    }
  }
  function closePreview() {
    setPreviewLink(null);
  }
  

 
  // 11) Upload für normale User
  async function handleUpload(e) {
    e.preventDefault();
    setUploadMessage('');

    if (!uploadDocName || !uploadFile) {
      setUploadMessage('Bitte Name + Datei auswählen.');
      return;
    }

    try {
      const form = new FormData();
      form.append('docName', uploadDocName);
      form.append('file', uploadFile);
      form.append('userMessage', uploadUserMessage);
      form.append('customerId', selectedCustomerId || '');

      const res = await fetch(`${API_URL}/api/uploadDocument`, {
        method: 'POST',
        body: form
      });
      if (!res.ok) throw new Error(await res.text());

      const js = await res.json();
      setUploadMessage(js.message || 'Hochgeladen!');
      setShowUploadModal(false);
      setLoading(true);
      // Cache leeren, damit neue Dokumente und Kunden sofort sichtbar sind
      invalidateCache(`${API_URL}/api/readvpdfs?customerId=all`);
      invalidateCache(`${API_URL}/api/readvpdfs?customerId=${selectedCustomerId}`);
      // Neu laden, damit das neue Dokument sofort sichtbar ist
      await fetchDocs();




      // Neu laden: API gibt Array zurück
      const resp2 = await fetch(
        `${API_URL}/api/readvpdfs?customerId=${isAdmin ? 'all' : selectedCustomerId}`
      );
      const newData = await resp2.json();
      if (Array.isArray(newData)) {
        setAllDocs(newData);
      } else {
        console.error('API hat kein Array zurückgegeben:', newData);
        setAllDocs([]);
      }

      setLoading(false);
    } catch (err) {
      setUploadMessage('Fehler: ' + err.message);
    }
  }

  // 12) Upload für Admin mit strukturierter Maske
  async function handleAdminUpload(e) {
    e.preventDefault();
    setUploadMessage('');

    // Dateigröße prüfen (4MB Limit)
    if (adminFile && adminFile.size > 4 * 1024 * 1024) {
      showToast(
        'Die Datei ist zu groß. Bitte wähle eine Datei unter 4MB.',
        'error'
      );
      return;
    }

    // Kunde bestimmen
    let customer = '';
    if (adminCustomer === '__newCustomer__') {
      if (!adminNewCustomer.trim()) {
        setUploadMessage('Bitte neuen Kunden eingeben.');
        return;
      }
      customer = adminNewCustomer.trim();
    } else if (adminCustomer) {
      customer = adminCustomer;
    } else {
      setUploadMessage('Bitte Kunde wählen.');
      return;
    }

    // Straße bestimmen
    let street = '';
    if (adminStreet === '__newStreet__') {
      if (!adminNewStreet.trim()) {
        setUploadMessage('Bitte neue Straße/HNr. eingeben.');
        return;
      }
      street = adminNewStreet.trim();
    } else if (adminStreet) {
      street = adminStreet;
    } else {
      setUploadMessage('Bitte Straße/HNr. wählen.');
      return;
    }

    if (!adminDocType) {
      setUploadMessage('Bitte Typ wählen oder neu anlegen.');
      return;
    }
    if (adminDocType === '__newDocType__' && !adminNewDocType.trim()) {
      setUploadMessage('Bitte neuen Typ eingeben.');
      return;
    }
    if (!adminLabel.trim()) {
      setUploadMessage('Bitte Kurzbezeichnung eingeben.');
      return;
    }
    if (!adminFile) {
      setUploadMessage('Bitte Datei auswählen.');
      return;
    }

    try {
      const type = adminDocType === '__newDocType__' ? adminNewDocType.trim() : adminDocType;
      const dateRaw = adminDate.replace(/-/g, '');
      const label = adminLabel.trim();
      const finalName = `readv_${customer}_${street}_${dateRaw}_${type}_${label}`;

      const form = new FormData();
      form.append('docName', finalName);
      form.append('file', adminFile, adminFile.name);
      form.append('userMessage', '');
      form.append('customerId', customer);

      const res = await fetch(`${API_URL}/api/uploadDocument`, {
        method: 'POST',
        body: form
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        let errorMessage = 'Netzwerkfehler beim Upload. Bitte versuche es später erneut.';
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.error) {
            errorMessage = errorJson.error;
            if (errorMessage.includes('groß') || errorMessage.includes('size') || errorMessage.includes('4MB')) {
              errorMessage = 'Die Datei ist zu groß. Bitte wähle eine Datei unter 4MB.';
            }
          }
        } catch (e) {
          // Fehler ist kein JSON, verwende Originaltext
          if (errorText.includes('groß') || errorText.includes('size') || errorText.includes('4MB')) {
            errorMessage = 'Die Datei ist zu groß. Bitte wähle eine Datei unter 4MB.';
          }
        }
        
        showToast(errorMessage, 'error');
        setUploadMessage(errorMessage);
        return;
      }
      
      const js = await res.json();
      
      // Erfolgs-Toast mit Details
      const fileSizeMB = (adminFile.size / (1024 * 1024)).toFixed(2);
      const toastMessage = `Dokument "${adminLabel.trim()}" wurde erfolgreich hochgeladen.\n\nKunde: ${customer}\nStraße: ${street}\nTyp: ${type}\nDatum: ${adminDate}\nGröße: ${fileSizeMB} MB`;
      
      showToast(toastMessage, 'success', js.webUrl || null);
      
      setUploadMessage(js.message || 'Admin: hochgeladen!');
      // Cache leeren, damit neue Dokumente und Kunden sofort sichtbar sind
      invalidateCache(`${API_URL}/api/readvpdfs?customerId=all`);
      invalidateCache(`${API_URL}/api/readvpdfs?customerId=${customer}`);
      // Neu laden, damit das neue Dokument sofort sichtbar ist
      await fetchDocs();



      // Reset Felder
      setAdminCustomer('');
      setAdminNewCustomer('');
      setAdminStreet('');
      setAdminNewStreet('');
      setAdminDate(new Date().toISOString().slice(0,10));
      setAdminDocType('');
      setAdminNewDocType('');
      setAdminLabel('');
      setAdminFile(null);
      setShowUploadModal(false);
      setLoading(true);

      // Neu laden: API gibt Array zurück
      const resp2 = await fetch(`${API_URL}/api/readvpdfs?customerId=all`);
      const newData = await resp2.json();
      if (Array.isArray(newData)) {
        setAllDocs(newData);
      } else {
        console.error('API hat kein Array zurückgegeben:', newData);
        setAllDocs([]);
      }

      setLoading(false);
    } catch (err) {
      let errorMessage = 'Netzwerkfehler beim Upload. Bitte versuche es später erneut.';
      if (err.message) {
        errorMessage = err.message;
        if (errorMessage.includes('groß') || errorMessage.includes('size') || errorMessage.includes('4MB')) {
          errorMessage = 'Die Datei ist zu groß. Bitte wähle eine Datei unter 4MB.';
        }
      }
      showToast(errorMessage, 'error');
      setUploadMessage('Fehler: ' + errorMessage);
    }
  }

  // Toast-Funktion für Benachrichtigungen
  function showToast(message, type = 'success', link = null) {
    setToast({ message, type, link });
    setTimeout(() => setToast(null), 15000); // 15 Sekunden
  }

  return (
    <div className="documents-container" style={{ paddingTop: '6rem', position: 'relative' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <h1>Dokumente</h1>
        <button className="btn-upload-docs" onClick={() => setShowUploadModal(true)}>
          + Dokumente hochladen
        </button>
      </div>

      {/* Preview-Info-Box für Admin */}
      <PreviewInfoBox 
        isAdmin={isAdmin} 
        isSummaryMode={isSummaryMode} 
        loading={loading} 
      />

      {loadError && (
        <div className="documents-load-error" role="alert">
          <strong>Dokumente konnten nicht geladen werden.</strong>
          <p>{loadError.message}</p>
          {loadError.code && <span className="documents-load-error-code">({loadError.code})</span>}
          <button type="button" className="btn-retry-docs" onClick={() => fetchDocs()}>Erneut versuchen</button>
        </div>
      )}

      {loading ? (
        <div className="loading-hint">Lade Dokumente…</div>
      ) : (
        <>
          {/* ========== FILTER-Bereich ========== */}
          <div className="documents-filters">
            <input
              type="text"
              placeholder="Suche…"
              className="filter-input"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {isAdmin && (
              <select
                className="filter-input"
                value={filterCustomer}
                onChange={e => setFilterCustomer(e.target.value)}
                title="Wählen Sie einen Kunden oder 'Alle Kunden' für Übersicht"
              >
                <option value="Alle Kunden">Alle Kunden</option>
                {docCustomers.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
            <select
              className="filter-input"
              value={filterStreet}
              onChange={e => {
                const newStreet = e.target.value;
                setFilterStreet(newStreet);
                // Wenn eine Straße ausgewählt wird (nicht "Alle Wohnungen"), 
                // schließe das Preview-Overlay falls offen
                if (newStreet !== 'Alle Wohnungen' && previewLink) {
                  closePreview();
                }
              }}
            >
              <option value="Alle Wohnungen">Alle Wohnungen</option>
              {docStreets.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              className="filter-input"
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
            >
              <option value="Alle Typen">Alle Typen</option>
              {docTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select
              className="filter-input"
              value={filterYear}
              onChange={e => setFilterYear(e.target.value)}
            >
              <option value="Alle Jahre">Alle Jahre</option>
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              className="filter-input"
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
            >
              <option value="Alle Monate">Alle Monate</option>
              {months.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* ========== DESKTOP-Tabelle ========== */}
          <div className={`documents-table desktop-only${isAdmin ? ' admin' : ''}${isSummaryMode ? ' summary-mode' : ''}`}>
            <div className="table-header">
              <span 
                className="col-docname sortable" 
                onClick={() => handleSort('docName')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Klicken zum Sortieren"
              >
                Dokumentenname {getSortIcon('docName')}
              </span>
              {isAdmin && (
                <span 
                  className="col-customer sortable" 
                  onClick={() => handleSort('customerId')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Klicken zum Sortieren"
                >
                  Kunde {getSortIcon('customerId')}
                </span>
              )}
              <span 
                className="col-street sortable" 
                onClick={() => handleSort('streetHn')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                title="Klicken zum Sortieren"
              >
                Straße/HNr. {getSortIcon('streetHn')}
              </span>
              {!isSummaryMode && (
                <>
                  <span 
                    className="col-type sortable" 
                    onClick={() => handleSort('docType')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Klicken zum Sortieren"
                  >
                    Typ {getSortIcon('docType')}
                  </span>
                  <span 
                    className="col-date sortable" 
                    onClick={() => handleSort('date')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    title="Klicken zum Sortieren"
                  >
                    Datum {getSortIcon('date')}
                  </span>
                  <span className="col-size">Größe</span>
                  <span className="col-actions">Aktionen</span>
                </>
              )}
            </div>

            {filteredDocs.slice(0, visibleCount).map((doc, idx) => {
              const sizeMB = doc.size ? (doc.size / (1024 * 1024)).toFixed(1) : '—';
              return (
                <div className="table-row" key={idx}>
                  <span className="col-docname">{doc.docName}</span>
                  {isAdmin && <span className="col-customer">{doc.customerId}</span>}
                  <span className="col-street">{doc.streetHn}</span>
                  {!isSummaryMode && (
                    <>
                      <span className="col-type">{doc.docType}</span>
                      <span className="col-date">{doc.dateDE}</span>
                      <span className="col-size">{sizeMB} {doc.size ? 'MB' : ''}</span>
                      <span className="col-actions">
                        <button
                          aria-label={`Vorschau anzeigen für ${doc.docName}`}
                          className="icon-button"
                          onClick={() => handlePreview(doc)}
                        >
                          <img src={previewIcon} alt="Vorschau-Icon" className="icon-img" />
                        </button>
                        <button
                          aria-label={`Download starten für ${doc.docName}`}
                          className="icon-button"
                          onClick={() => handleDownload(doc)}
                        >
                          <img src={downloadIcon} alt="Download-Icon" className="icon-img" />
                        </button>
                      </span>
                    </>
                  )}
                </div>
              );
            })}

            {visibleCount < filteredDocs.length && (
              // Sentinel-Element für Intersection Observer
              <div ref={loadMoreRef} className="loading-more">
                Weitere Dokumente laden…
              </div>
            )}
          </div>

          {/* ========== MOBILE-KARTEN ========== */}
          <div className="doc-cards-grid mobile-only">
            {filteredDocs.slice(0, visibleCount).map((doc, idx) => {
              const sizeMB = doc.size ? (doc.size / (1024 * 1024)).toFixed(1) : '—';
              return (
                <div className="doc-card" key={idx}>
                  <div className="doc-card-header">
                    <h3 className="doc-title">{doc.docName}</h3>
                    {!isSummaryMode && <span className="doc-badge">{doc.docType}</span>}
                  </div>
                  {!isSummaryMode && (
                    <p className="doc-subinfo">
                      {isAdmin && <><strong>{doc.customerId}</strong> – </>}
                      {doc.streetHn} – {doc.dateDE} – {sizeMB} {doc.size ? 'MB' : ''}
                    </p>
                  )}
                  {isAdmin && isSummaryMode && (
                    <p className="doc-subinfo">
                      <strong>{doc.customerId}</strong> – {doc.streetHn}
                    </p>
                  )}
                  {!isSummaryMode && (
                    <div className="doc-actions">
                      <button
                        aria-label={`Vorschau anzeigen für ${doc.docName}`}
                        className="icon-button"
                        onClick={() => handlePreview(doc)}
                      >
                        <img src={previewIcon} alt="Vorschau-Icon" className="icon-img" />
                        <span>Vorschau</span>
                      </button>
                      <button
                        aria-label={`Download starten für ${doc.docName}`}
                        className="icon-button"
                        onClick={() => handleDownload(doc)}
                      >
                        <img src={downloadIcon} alt="Download-Icon" className="icon-img" />
                        <span>Download</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ========== Kein Dokument ========== */}
          {!filteredDocs.length && (
            <div className="no-docs">Keine Dokumente gefunden.</div>
          )}

          {/* ========== Dokumentenzähler ========== */}
          {!!filteredDocs.length && (
            <div className="docs-count">
              {Math.min(visibleCount, filteredDocs.length)} von {filteredDocs.length} Dokument(e) angezeigt
            </div>
          )}
        </>
      )}

      {/* ========== PREVIEW OVERLAY ========== */}
      {previewLink && (
        <div className="preview-overlay" onClick={closePreview}>
          <div className="preview-modal" onClick={e => e.stopPropagation()}>
            <button className="close-preview" onClick={closePreview}>×</button>
            <div className="preview-content">
              <iframe
                src={previewLink}
                title="PDF Vorschau"
                frameBorder="0"
                className="preview-iframe"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}

      {/* ========== UPLOAD MODAL ========== */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal" onClick={e => e.stopPropagation()}>
            <button className="close-upload-modal" onClick={() => setShowUploadModal(false)}>×</button>

            <h2>{isAdmin ? 'Admin: Neues Dokument hochladen' : 'Neues Dokument hochladen'}</h2>
            {uploadMessage && <p className="upload-message">{uploadMessage}</p>}

            {isAdmin ? (
              <form onSubmit={handleAdminUpload} className="upload-form">
                <div className="upload-form-group">
                  <label htmlFor="admin-customer-select">Kunde</label>
                  <select
                    id="admin-customer-select"
                    required
                    value={adminCustomer}
                    onChange={e => setAdminCustomer(e.target.value)}
                  >
                    <option value="">— Kunde wählen —</option>
                    {docCustomers.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="__newCustomer__">Neuen Kunden anlegen…</option>
                  </select>
                </div>
                {adminCustomer === '__newCustomer__' && (
                  <div className="upload-form-group">
                    <label htmlFor="admin-new-customer">Neuer Kunde</label>
                    <input
                      id="admin-new-customer"
                      required
                      type="text"
                      value={adminNewCustomer}
                      onChange={e => setAdminNewCustomer(e.target.value)}
                      placeholder="z.B. Kunde 10001"
                    />
                  </div>
                )}
                <div className="upload-form-group">
                  <label htmlFor="admin-street-select">Straße / HNr.</label>
                  <select
                    id="admin-street-select"
                    required
                    value={adminStreet}
                    onChange={e => setAdminStreet(e.target.value)}
                  >
                    <option value="">— wählen —</option>
                    {docStreets.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                    <option value="__newStreet__">Neue Straße/HNr. anlegen…</option>
                  </select>
                </div>
                {adminStreet === '__newStreet__' && (
                  <div className="upload-form-group">
                    <label htmlFor="admin-new-street">Neue Straße / HNr.</label>
                    <input
                      id="admin-new-street"
                      required
                      type="text"
                      value={adminNewStreet}
                      onChange={e => setAdminNewStreet(e.target.value)}
                      placeholder="z.B. Gartenstraße 5a"
                    />
                  </div>
                )}
                <div className="upload-form-group">
                  <label htmlFor="admin-date">Datum</label>
                  <input
                    id="admin-date"
                    required
                    type="date"
                    value={adminDate}
                    onChange={e => setAdminDate(e.target.value)}
                  />
                </div>
                <div className="upload-form-group">
                  <label htmlFor="admin-doc-type-select">Typ</label>
                  <select
                    id="admin-doc-type-select"
                    required
                    value={adminDocType}
                    onChange={e => setAdminDocType(e.target.value)}
                  >
                    <option value="">— Typ wählen —</option>
                    {docTypes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                    <option value="__newDocType__">Neuen Typ anlegen…</option>
                  </select>
                </div>
                {adminDocType === '__newDocType__' && (
                  <div className="upload-form-group">
                    <label htmlFor="admin-new-doc-type">Neuer Typ</label>
                    <input
                      id="admin-new-doc-type"
                      required
                      type="text"
                      value={adminNewDocType}
                      onChange={e => setAdminNewDocType(e.target.value)}
                      placeholder="z.B. Vertrag"
                    />
                  </div>
                )}
                <div className="upload-form-group">
                  <label htmlFor="admin-label">Kurzbezeichnung</label>
                  <input
                    id="admin-label"
                    required
                    type="text"
                    value={adminLabel}
                    onChange={e => setAdminLabel(e.target.value)}
                    placeholder="z.B. Strom Mai 2025"
                  />
                </div>
                <div className="upload-form-group">
                  <label htmlFor="admin-file-input">Datei auswählen</label>
                  <input
                    id="admin-file-input"
                    required
                    type="file"
                    accept=".pdf,image/*"
                    onChange={e => setAdminFile(e.target.files[0])}
                  />
                </div>
                <button type="submit" className="btn-submit-upload">Hochladen</button>
              </form>
            ) : (
              <form onSubmit={handleUpload} className="upload-form">
                <div className="upload-form-group">
                  <label htmlFor="user-doc-name">Dokumentenname</label>
                  <input
                    id="user-doc-name"
                    required
                    type="text"
                    value={uploadDocName}
                    onChange={e => setUploadDocName(e.target.value)}
                    placeholder="z.B. Mietvertrag"
                  />
                </div>
                <div className="upload-form-group">
                  <label htmlFor="user-message">Bemerkungen (optional)</label>
                  <textarea
                    id="user-message"
                    value={uploadUserMessage}
                    onChange={e => setUploadUserMessage(e.target.value)}
                    placeholder="z.B. wichtige Infos…"
                  />
                </div>
                <div className="upload-form-group">
                  <label htmlFor="user-file-input">Datei auswählen</label>
                  <input
                    id="user-file-input"
                    required
                    type="file"
                    accept=".pdf,image/*"
                    onChange={e => setUploadFile(e.target.files[0])}
                  />
                </div>
                <button type="submit" className="btn-submit-upload">Hochladen</button>
              </form>
            )}
          </div>
        </div>
      )}
      
      {/* Toast-Benachrichtigung */}
      <ToastNotification toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}